import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';

const auth = firebase.auth();
const db = firebase.firestore();

/**
 * @typedef {Object} UserProfile
 * @property {string} uid
 */

/**
 * Custom hook for authentication state management
 * Handles user authentication, profile fetching, and auth state changes
 * 
 * @returns {{
 *   currentUser: firebase.User | null,
 *   userProfile: UserProfile | null,
 *   loading: boolean,
 *   handleLogout: () => Promise<void>
 * }}
 */
export function useAuth() {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          // Force token refresh to get latest custom claims
          await user.getIdToken(true);

          const userDoc = await db.collection('users').doc(user.uid).get();

          if (!mounted) return;
          if (userDoc.exists) {
            setUserProfile({ uid: user.uid, ...userDoc.data() });
            setCurrentUser(user);
          } else {
            // Retry after 3 seconds (waiting for onUserCreate trigger)
            await new Promise(resolve => setTimeout(resolve, 3000));
            if (!mounted) return;
            const retryDoc = await db.collection('users').doc(user.uid).get();

            if (retryDoc.exists) {
              setUserProfile({ uid: user.uid, ...retryDoc.data() });
              setCurrentUser(user);
            } else {
              // Profile truly missing — log out; transient errors below do not log out
              await auth.signOut();
            }
          }
        } catch (error) {
          // Transient Firestore/network failure: stay logged in, keep old profile
          if (error instanceof Error) {
            // Safe logging placeholder
          }
        }
      } else {
        if (!mounted) return;
        setCurrentUser(null);
        setUserProfile(null);
      }
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      if (error instanceof Error) {
        // Safe logging placeholder
      }
    }
  };

  return {
    currentUser,
    userProfile,
    loading,
    handleLogout
  };
}
